import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { QualityBadge, QualityDetails } from '../components/QualityScore';
import { fetchReferenceLibrary, type BrandReferenceLibrary } from '../lib/brandLibrary';
import {
    createAdCreatorGenerationEventSource,
    getAdCreatorGenerationResult,
    postAdCreatorGenerateFull,
    postAdCreatorPmaxPlan,
    type GenerationAssetSnapshot,
    type GenerationEventPayload,
    type GenerationPhase,
    type GenerationStatus,
    type PmaxPlanResponse
} from '../lib/adCreator';

type PmaxEntryPoint = '媽媽族群' | '瘦身減肥' | '怕熱女孩' | '腰痠背痛' | '名媛貴婦';
type SpecializationType = 'Persona特式化' | '活動特式化' | '見證代言特式化';

const GENERATION_PHASE_LABELS: Record<GenerationPhase, string> = {
    queued: '已建立工作，等待 Cloudflare Workflow 啟動',
    strategy_analysis: '策略分析中：正在建立 PMAX/RSA 共用策略、素材 slot 與 proof plan，尚未開始單句文案',
    asset_generation: '單句生成中：正在逐一處理 H1 / H2 / H3 / D asset',
    history_persistence: '寫入生成紀錄中',
    completed: '全部 asset 已完成',
    system_fault: '系統層錯誤，流程已中止'
};

interface QualityScore {
    overall: number;
    dimensions: {
        adJargon: number;
        readability: number;
        attractiveness: number;
        personaMatch: number;
        creativity: number;
    };
    details: {
        clichesFound: string[];
        emotionalScore: number;
        actionScore: number;
        sentenceComplexity: number;
        keywordMatch: number;
    };
    suggestions: string[];
}

interface Headline {
    id: string;
    type: 'H1' | 'H2' | 'H3';
    text: string;
    charCount: number;
    keyword?: string;
    usp?: string;
    cta?: string;
    isCompliant: boolean;
    qualityScore?: QualityScore;
}

interface LongHeadline {
    id: string;
    text: string;
    charCount: number;
}

interface Description {
    id: string;
    variant: string;
    text: string;
    charCount: number;
    keyword: string;
    isCompliant: boolean;
    issues?: string[];
    qualityScore?: QualityScore;
}

interface MarketingDescription {
    id: string;
    text: string;
    charCount: number;
}

interface GeneratedAssets {
    headlines: Headline[];
    descriptions: Description[];
    longHeadlines?: LongHeadline[];
    marketingDescriptions?: MarketingDescription[];
}

interface GenerationMetadata {
    generationTime: number;
    totalLLMCalls: number;
    modelUsed: string;
    strategyUsed?: {
        h1Specialization: string;
        situationProblem: string;
        h2Feature: string;
        h3ProofType: string;
        h2Technologies: string[];
        h3ProofCombinations: string[];
    };
    quality?: {
        avgHeadlineScore: number;
        avgDescriptionScore: number;
        topHeadlineId: string;
        topDescriptionId: string;
    };
}

type PmaxPlan = NonNullable<PmaxPlanResponse['plan']>;

const ENTRY_POINTS: Array<{
    id: PmaxEntryPoint;
    title: string;
    description: string;
}> = [
    { id: '媽媽族群', title: '媽媽族群', description: '人生階段改變後的身形與日常入口' },
    { id: '瘦身減肥', title: '瘦身減肥', description: '結果導向的體態目標入口' },
    { id: '怕熱女孩', title: '怕熱女孩', description: '先解除悶熱與穿不住的抗拒點' },
    { id: '腰痠背痛', title: '腰痠背痛', description: '先承接身體負擔與支撐需求' },
    { id: '名媛貴婦', title: '名媛貴婦', description: '場合、體面與高級感入口' }
];

const SPECIALIZATIONS: Array<{ id: SpecializationType; description: string }> = [
    { id: 'Persona特式化', description: '從具體生活情境與當下困擾開場' },
    { id: '活動特式化', description: '從節慶活動與損失感切入' },
    { id: '見證代言特式化', description: '從推薦、比較與見證證據切入' }
];

function parseMultilineList(text: string): string[] {
    return Array.from(new Set(
        text
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
    ));
}

function stringifyList(values: string[]): string {
    return values.join('\n');
}

function toCreativePackText(plan: PmaxPlan | null, assets: GeneratedAssets | null): string {
    if (!assets) return '';
    const shortHeadlines = assets.headlines.map((item, index) => `${index + 1}. ${item.text}`).join('\n');
    const longHeadlines = (assets.longHeadlines || []).map((item, index) => `${index + 1}. ${item.text}`).join('\n');
    const marketingDescriptions = (assets.marketingDescriptions || []).map((item, index) => `${index + 1}. ${item.text}`).join('\n');
    const searchThemes = plan ? plan.searchSignalPlan.searchThemes.join('、') : '';
    const audienceSignals = plan ? plan.searchSignalPlan.audienceSignals.join('、') : '';

    return [
        plan ? `Asset Group: ${plan.assetGroupPlan.themeName}` : '',
        plan ? `Landing Page: ${plan.landingPagePlan.title} ${plan.landingPagePlan.url}` : '',
        searchThemes ? `Search Themes: ${searchThemes}` : '',
        audienceSignals ? `Audience Signals: ${audienceSignals}` : '',
        '',
        'Short Headlines (15)',
        shortHeadlines,
        '',
        'Long Headlines (5)',
        longHeadlines,
        '',
        'Descriptions (5)',
        marketingDescriptions
    ].filter(Boolean).join('\n');
}

function SectionTitle({ children }: { children: string }) {
    return (
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 700 }}>
            {children}
        </h3>
    );
}

function SurfaceCard({ children }: { children: ReactNode }) {
    return (
        <div
            style={{
                border: '1px solid var(--color-border)',
                borderRadius: '16px',
                background: 'var(--color-bg)',
                padding: '1rem'
            }}
        >
            {children}
        </div>
    );
}

export function PmaxAdCreator() {
    const streamRef = useRef<EventSource | null>(null);
    const resultPollRef = useRef<number | null>(null);
    const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
    const [referenceLibrary, setReferenceLibrary] = useState<BrandReferenceLibrary | null>(null);
    const [planLoading, setPlanLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const [entryPoint, setEntryPoint] = useState<PmaxEntryPoint>('媽媽族群');
    const [selectedScenarioKey, setSelectedScenarioKey] = useState('');
    const [selectedLandingPageId, setSelectedLandingPageId] = useState('');
    const [specialization, setSpecialization] = useState<SpecializationType>('Persona特式化');
    const [seedKeywordInput, setSeedKeywordInput] = useState('');
    const [seedKeywords, setSeedKeywords] = useState<string[]>([]);
    const [metaPostText, setMetaPostText] = useState('');
    const [metaPostUrl, setMetaPostUrl] = useState('');
    const [celebrityNames, setCelebrityNames] = useState<string[]>([]);
    const [activityNames, setActivityNames] = useState<string[]>([]);

    const [plan, setPlan] = useState<PmaxPlan | null>(null);
    const [searchThemesText, setSearchThemesText] = useState('');
    const [audienceSignalsText, setAudienceSignalsText] = useState('');
    const [generatedAssets, setGeneratedAssets] = useState<GeneratedAssets | null>(null);
    const [metadata, setMetadata] = useState<GenerationMetadata | null>(null);
    const [generationId, setGenerationId] = useState('');
    const [generationStatus, setGenerationStatus] = useState<GenerationStatus>('queued');
    const [generationPhase, setGenerationPhase] = useState<GenerationPhase>('queued');
    const [assetProgress, setAssetProgress] = useState<GenerationAssetSnapshot[]>([]);

    useEffect(() => {
        void loadReferenceLibrary();
        return () => {
            streamRef.current?.close();
            streamRef.current = null;
            if (resultPollRef.current !== null) {
                window.clearInterval(resultPollRef.current);
                resultPollRef.current = null;
            }
        };
    }, []);

    async function loadReferenceLibrary() {
        try {
            const library = await fetchReferenceLibrary();
            setReferenceLibrary(library);
        } catch (err) {
            setError(err instanceof Error ? err.message : '無法載入品牌資料庫');
        }
    }

    function addSeedKeyword() {
        const trimmed = seedKeywordInput.trim();
        if (!trimmed) return;
        if (!seedKeywords.includes(trimmed)) {
            setSeedKeywords((prev) => [...prev, trimmed]);
        }
        setSeedKeywordInput('');
    }

    function removeSeedKeyword(keyword: string) {
        setSeedKeywords((prev) => prev.filter((item) => item !== keyword));
    }

    function toggleNamedValue(target: string, values: string[], setter: (next: string[]) => void) {
        if (values.includes(target)) {
            setter(values.filter((value) => value !== target));
            return;
        }
        setter([...values, target]);
    }

    function upsertAssetProgress(nextAsset: GenerationAssetSnapshot) {
        setAssetProgress((prev) => {
            const hasExisting = prev.some((asset) => asset.assetId === nextAsset.assetId);
            if (!hasExisting) return [...prev, nextAsset];
            return prev.map((asset) => asset.assetId === nextAsset.assetId ? nextAsset : asset);
        });
    }

    function bindGenerationStream(nextGenerationId: string) {
        streamRef.current?.close();
        if (resultPollRef.current !== null) {
            window.clearInterval(resultPollRef.current);
            resultPollRef.current = null;
        }
        const source = createAdCreatorGenerationEventSource(nextGenerationId);
        streamRef.current = source;

        const closeWatchers = () => {
            source.close();
            streamRef.current = null;
            if (resultPollRef.current !== null) {
                window.clearInterval(resultPollRef.current);
                resultPollRef.current = null;
            }
        };

        const applyResultSnapshot = (result: Awaited<ReturnType<typeof getAdCreatorGenerationResult<GeneratedAssets, GenerationMetadata, PmaxPlan>>>) => {
            if (!result.success) {
                setError(result.error || '查無生成狀態，後端工作狀態遺失');
                setGenerating(false);
                closeWatchers();
                return;
            }
            if (result.state?.assets) setAssetProgress(result.state.assets);
            if (result.state?.phase) setGenerationPhase(result.state.phase);
            if (result.generated) setGeneratedAssets(result.generated);
            if (result.metadata) setMetadata(result.metadata);
            if (result.pmaxPlan && typeof result.pmaxPlan === 'object') {
                setPlan(result.pmaxPlan as PmaxPlan);
            }
            setGenerationStatus(result.status);
            if (result.status === 'completed') {
                setGenerating(false);
                closeWatchers();
                return;
            }
            if (result.status === 'system_fault') {
                setError(result.error || '生成流程中斷');
                setGenerating(false);
                closeWatchers();
            }
        };

        resultPollRef.current = window.setInterval(() => {
            void (async () => {
                try {
                    const result = await getAdCreatorGenerationResult<GeneratedAssets, GenerationMetadata, PmaxPlan>(nextGenerationId);
                    applyResultSnapshot(result);
                } catch (err) {
                    setError(err instanceof Error ? err.message : '查詢生成狀態失敗');
                    setGenerating(false);
                    closeWatchers();
                }
            })();
        }, 2000);

        const eventTypes: Array<GenerationEventPayload['type']> = [
            'generation_started',
            'asset_started',
            'asset_rejected',
            'asset_regenerated',
            'asset_completed',
            'set_validation_failed',
            'dependency_invalidated',
            'completed',
            'system_fault'
        ];

        const handleEvent = (payload: GenerationEventPayload<GeneratedAssets, GenerationMetadata, PmaxPlan>) => {
            if (payload.status) setGenerationStatus(payload.status);
            if (payload.phase) setGenerationPhase(payload.phase);
            if (payload.asset) upsertAssetProgress(payload.asset);
            if (payload.generated) setGeneratedAssets(payload.generated);
            if (payload.metadata) setMetadata(payload.metadata);
            if (payload.pmaxPlan && typeof payload.pmaxPlan === 'object') {
                setPlan(payload.pmaxPlan as PmaxPlan);
            }
            if (payload.type === 'system_fault') {
                setError(payload.error || '生成流程中斷');
                setGenerating(false);
                closeWatchers();
            }
            if (payload.type === 'completed') {
                setGenerating(false);
                closeWatchers();
            }
        };

        eventTypes.forEach((eventType) => {
            source.addEventListener(eventType, (event) => {
                const messageEvent = event as MessageEvent<string>;
                handleEvent(JSON.parse(messageEvent.data) as GenerationEventPayload<GeneratedAssets, GenerationMetadata, PmaxPlan>);
            });
        });

        source.onerror = () => {
            void (async () => {
                try {
                    const result = await getAdCreatorGenerationResult<GeneratedAssets, GenerationMetadata, PmaxPlan>(nextGenerationId);
                    applyResultSnapshot(result);
                } catch (err) {
                    setError(err instanceof Error ? err.message : '串流連線失敗');
                    setGenerating(false);
                    closeWatchers();
                }
            })();
        };
    }

    async function buildPlan(nextStep?: 2 | 3 | 4) {
        try {
            setPlanLoading(true);
            setError('');
            const response = await postAdCreatorPmaxPlan({
                keywords: seedKeywords,
                specialization,
                metaPostText,
                pmaxContext: {
                    entryPoint,
                    ...(selectedScenarioKey ? { scenarioKey: selectedScenarioKey } : {}),
                    ...(selectedLandingPageId ? { landingPageId: selectedLandingPageId } : {}),
                    ...(parseMultilineList(searchThemesText).length > 0 ? { searchThemes: parseMultilineList(searchThemesText) } : {}),
                    ...(parseMultilineList(audienceSignalsText).length > 0 ? { audienceSignals: parseMultilineList(audienceSignalsText) } : {})
                }
            });

            if (!response.success || !response.plan) {
                setError(response.error || '無法建立 PMAX 規劃');
                return;
            }

            setPlan(response.plan);
            setSelectedScenarioKey(response.plan.scenarioPlan.scenarioKey);
            setSelectedLandingPageId(response.plan.landingPagePlan.landingPageId);
            setSpecialization(response.plan.generationContext.specialization as SpecializationType);
            setSearchThemesText(stringifyList(response.plan.searchSignalPlan.searchThemes));
            setAudienceSignalsText(stringifyList(response.plan.searchSignalPlan.audienceSignals));
            if (nextStep) setStep(nextStep);
        } catch (err) {
            setError(err instanceof Error ? err.message : '建立 PMAX 規劃時發生錯誤');
        } finally {
            setPlanLoading(false);
        }
    }

    async function generateAssets() {
        if (!plan) {
            setError('請先完成 PMAX 規劃');
            return;
        }

        const searchThemes = parseMultilineList(searchThemesText);
        const audienceSignals = parseMultilineList(audienceSignalsText);
        if (searchThemes.length === 0) {
            setError('至少要保留一個搜尋主題');
            return;
        }

        try {
            setGenerating(true);
            setError('');
            setGeneratedAssets(null);
            setMetadata(null);
            setGenerationId('');
            setGenerationStatus('queued');
            setGenerationPhase('queued');
            setAssetProgress([]);
            const response = await postAdCreatorGenerateFull<GeneratedAssets, GenerationMetadata>({
                keywords: searchThemes,
                persona: plan.generationContext.persona,
                scene: plan.generationContext.scene,
                specialization,
                celebrityNames,
                activityNames,
                metaPostUrl,
                metaPostText,
                pmaxPlanId: plan.pmaxPlanId,
                pmaxContext: {
                    entryPoint,
                    scenarioKey: selectedScenarioKey,
                    landingPageId: selectedLandingPageId,
                    searchThemes,
                    audienceSignals
                }
            });

            if (!response.success || !response.generationId) {
                setError(response.error || '生成失敗');
                setGenerating(false);
                return;
            }

            setGenerationId(response.generationId);
            setGenerationStatus(response.status);
            setGenerationPhase(response.state.phase || 'queued');
            setAssetProgress(response.state.assets || []);
            setStep(5);
            bindGenerationStream(response.generationId);
        } catch (err) {
            setError(err instanceof Error ? err.message : '生成文案時發生錯誤');
            setGenerating(false);
        }
    }

    async function copyCreativePack() {
        if (!generatedAssets) return;
        try {
            await navigator.clipboard.writeText(toCreativePackText(plan, generatedAssets));
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (err) {
            console.error('copy failed', err);
        }
    }

    function resetPlanner() {
        streamRef.current?.close();
        streamRef.current = null;
        if (resultPollRef.current !== null) {
            window.clearInterval(resultPollRef.current);
            resultPollRef.current = null;
        }
        setStep(1);
        setPlan(null);
        setGeneratedAssets(null);
        setMetadata(null);
        setGenerationId('');
        setGenerationStatus('queued');
        setGenerationPhase('queued');
        setAssetProgress([]);
        setSelectedScenarioKey('');
        setSelectedLandingPageId('');
        setSearchThemesText('');
        setAudienceSignalsText('');
        setError('');
    }

    const scenarioOptions = useMemo(() => plan?.availableScenarios || [], [plan]);
    const landingPageOptions = useMemo(() => plan?.candidateLandingPages || [], [plan]);
    const currentScenario = useMemo(
        () => scenarioOptions.find((candidate) => candidate.key === selectedScenarioKey),
        [scenarioOptions, selectedScenarioKey]
    );

    return (
        <div style={{ padding: '2rem', maxWidth: '1240px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 800 }}>PMAX 入口先行文案工具</h1>
                    <p style={{ margin: '0.5rem 0 0', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                        先決定進場入口，再安排需求場景、Landing Page、搜尋主題、目標對象信號與 Creative Pack。
                    </p>
                </div>
                <button
                    onClick={resetPlanner}
                    style={{
                        border: '1px solid var(--color-border)',
                        background: 'transparent',
                        borderRadius: '999px',
                        padding: '0.7rem 1rem',
                        cursor: 'pointer'
                    }}
                >
                    重新開始
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '1rem' }}>
                <SurfaceCard>
                    <SectionTitle>流程</SectionTitle>
                    {[
                        '1. 選進場入口',
                        '2. 選需求場景',
                        '3. 確認 Landing Page',
                        '4. 檢視搜尋主題與訊號',
                        '5. 生成 Creative Pack'
                    ].map((label, index) => {
                        const current = step === index + 1;
                        return (
                            <div
                                key={label}
                                style={{
                                    padding: '0.75rem 0.9rem',
                                    borderRadius: '12px',
                                    marginBottom: '0.5rem',
                                    background: current ? 'var(--color-primary-light)' : 'transparent',
                                    border: `1px solid ${current ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                    fontWeight: current ? 700 : 500
                                }}
                            >
                                {label}
                            </div>
                        );
                    })}
                </SurfaceCard>

                <div style={{ display: 'grid', gap: '1rem' }}>
                    {step === 1 && (
                        <SurfaceCard>
                            <SectionTitle>Step 1｜選進場入口</SectionTitle>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                                {ENTRY_POINTS.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => {
                                            setEntryPoint(item.id);
                                            setSelectedScenarioKey('');
                                            setSelectedLandingPageId('');
                                            setPlan(null);
                                            setGeneratedAssets(null);
                                        }}
                                        style={{
                                            textAlign: 'left',
                                            borderRadius: '16px',
                                            border: `2px solid ${entryPoint === item.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                            background: entryPoint === item.id ? 'var(--color-primary-light)' : 'var(--color-bg)',
                                            padding: '1rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ fontWeight: 800, marginBottom: '0.35rem' }}>{item.title}</div>
                                        <div style={{ color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{item.description}</div>
                                    </button>
                                ))}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>補充搜尋語言</label>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <input
                                            value={seedKeywordInput}
                                            onChange={(e) => setSeedKeywordInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                                    e.preventDefault();
                                                    addSeedKeyword();
                                                }
                                            }}
                                            placeholder="例如：夏天塑身衣、洋裝內搭"
                                            style={{
                                                flex: 1,
                                                padding: '0.8rem 0.9rem',
                                                borderRadius: '12px',
                                                border: '1px solid var(--color-border)'
                                            }}
                                        />
                                        <button
                                            onClick={addSeedKeyword}
                                            style={{
                                                border: 'none',
                                                background: 'var(--color-primary)',
                                                color: '#fff',
                                                borderRadius: '12px',
                                                padding: '0 1rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            加入
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {seedKeywords.map((keyword) => (
                                            <button
                                                key={keyword}
                                                onClick={() => removeSeedKeyword(keyword)}
                                                style={{
                                                    border: '1px solid var(--color-border)',
                                                    background: 'var(--color-bg-secondary)',
                                                    borderRadius: '999px',
                                                    padding: '0.35rem 0.7rem',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {keyword} ×
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>特式化策略</label>
                                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                                        {SPECIALIZATIONS.map((item) => (
                                            <button
                                                key={item.id}
                                                onClick={() => setSpecialization(item.id)}
                                                style={{
                                                    textAlign: 'left',
                                                    borderRadius: '12px',
                                                    border: `1px solid ${specialization === item.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                    background: specialization === item.id ? 'var(--color-primary-light)' : 'var(--color-bg)',
                                                    padding: '0.75rem 0.9rem',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{ fontWeight: 700 }}>{item.id}</div>
                                                <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.2rem' }}>{item.description}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>Meta 貼文文字</label>
                                    <textarea
                                        value={metaPostText}
                                        onChange={(e) => setMetaPostText(e.target.value)}
                                        placeholder="貼入 Meta 內文，讓 planner 補抓情境信號"
                                        rows={7}
                                        style={{
                                            width: '100%',
                                            borderRadius: '12px',
                                            border: '1px solid var(--color-border)',
                                            padding: '0.8rem 0.9rem',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>Meta 貼文連結</label>
                                    <input
                                        type="url"
                                        value={metaPostUrl}
                                        onChange={(e) => setMetaPostUrl(e.target.value)}
                                        placeholder="https://www.facebook.com/..."
                                        style={{
                                            width: '100%',
                                            borderRadius: '12px',
                                            border: '1px solid var(--color-border)',
                                            padding: '0.8rem 0.9rem',
                                            marginBottom: '1rem'
                                        }}
                                    />
                                    <div style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                                        Step 1 只先決定入口與規劃方向。真正的搜尋主題與目標對象信號會在下一步由 planner 組裝。
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => void buildPlan(2)}
                                disabled={planLoading}
                                style={{
                                    border: 'none',
                                    background: 'var(--color-primary)',
                                    color: '#fff',
                                    borderRadius: '14px',
                                    padding: '0.9rem 1.2rem',
                                    cursor: planLoading ? 'not-allowed' : 'pointer',
                                    opacity: planLoading ? 0.6 : 1
                                }}
                            >
                                {planLoading ? '建立中...' : '產生入口規劃'}
                            </button>
                        </SurfaceCard>
                    )}

                    {step === 2 && (
                        <SurfaceCard>
                            <SectionTitle>Step 2｜選需求場景</SectionTitle>
                            <div style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                                同一個進場入口下還要再拆成具體場景，不能直接把所有需求混進同一個 asset group。
                            </div>
                            <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
                                {scenarioOptions.map((scenario) => (
                                    <button
                                        key={scenario.key}
                                        onClick={() => setSelectedScenarioKey(scenario.key)}
                                        style={{
                                            textAlign: 'left',
                                            borderRadius: '14px',
                                            border: `2px solid ${selectedScenarioKey === scenario.key ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                            background: selectedScenarioKey === scenario.key ? 'var(--color-primary-light)' : 'var(--color-bg)',
                                            padding: '0.9rem 1rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ fontWeight: 800 }}>{scenario.label}</div>
                                        <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>場景：{scenario.scene}</div>
                                    </button>
                                ))}
                            </div>
                            {plan && (
                                <div style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                                    目前推薦：{plan.scenarioPlan.customerState}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button
                                    onClick={() => setStep(1)}
                                    style={{
                                        border: '1px solid var(--color-border)',
                                        background: 'transparent',
                                        borderRadius: '12px',
                                        padding: '0.8rem 1rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    上一步
                                </button>
                                <button
                                    onClick={() => void buildPlan(3)}
                                    disabled={!selectedScenarioKey || planLoading}
                                    style={{
                                        border: 'none',
                                        background: 'var(--color-primary)',
                                        color: '#fff',
                                        borderRadius: '12px',
                                        padding: '0.8rem 1rem',
                                        cursor: !selectedScenarioKey || planLoading ? 'not-allowed' : 'pointer',
                                        opacity: !selectedScenarioKey || planLoading ? 0.6 : 1
                                    }}
                                >
                                    確認場景並配對 LP
                                </button>
                            </div>
                        </SurfaceCard>
                    )}

                    {step === 3 && plan && (
                        <SurfaceCard>
                            <SectionTitle>Step 3｜確認 Landing Page</SectionTitle>
                            <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
                                {landingPageOptions.map((page) => (
                                    <button
                                        key={page.id}
                                        onClick={() => setSelectedLandingPageId(page.id)}
                                        style={{
                                            textAlign: 'left',
                                            borderRadius: '14px',
                                            border: `2px solid ${selectedLandingPageId === page.id ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                            background: selectedLandingPageId === page.id ? 'var(--color-primary-light)' : 'var(--color-bg)',
                                            padding: '0.9rem 1rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ fontWeight: 800 }}>{page.title}</div>
                                        <div style={{ marginTop: '0.25rem', color: 'var(--color-text-secondary)' }}>{page.url}</div>
                                        <div style={{ marginTop: '0.25rem', color: 'var(--color-text-secondary)' }}>Hero：{page.heroAngle}</div>
                                    </button>
                                ))}
                            </div>
                            <div
                                style={{
                                    borderRadius: '14px',
                                    background: plan.landingPagePlan.matchStatus === 'mismatch' ? '#fff5f5' : 'var(--color-bg-secondary)',
                                    padding: '1rem',
                                    marginBottom: '1rem'
                                }}
                            >
                                <div style={{ fontWeight: 800, marginBottom: '0.35rem' }}>
                                    目前匹配狀態：{plan.landingPagePlan.matchStatus}
                                </div>
                                <div style={{ lineHeight: 1.6 }}>{plan.landingPagePlan.matchReason}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button
                                    onClick={() => setStep(2)}
                                    style={{
                                        border: '1px solid var(--color-border)',
                                        background: 'transparent',
                                        borderRadius: '12px',
                                        padding: '0.8rem 1rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    上一步
                                </button>
                                <button
                                    onClick={() => void buildPlan(4)}
                                    disabled={!selectedLandingPageId || planLoading}
                                    style={{
                                        border: 'none',
                                        background: 'var(--color-primary)',
                                        color: '#fff',
                                        borderRadius: '12px',
                                        padding: '0.8rem 1rem',
                                        cursor: !selectedLandingPageId || planLoading ? 'not-allowed' : 'pointer',
                                        opacity: !selectedLandingPageId || planLoading ? 0.6 : 1
                                    }}
                                >
                                    帶入搜尋主題與訊號
                                </button>
                            </div>
                        </SurfaceCard>
                    )}

                    {step === 4 && plan && (
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <SurfaceCard>
                                <SectionTitle>Step 4｜檢視搜尋主題 / 目標對象信號 / Asset Group Brief</SectionTitle>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>搜尋主題</label>
                                        <textarea
                                            rows={8}
                                            value={searchThemesText}
                                            onChange={(e) => setSearchThemesText(e.target.value)}
                                            style={{
                                                width: '100%',
                                                borderRadius: '12px',
                                                border: '1px solid var(--color-border)',
                                                padding: '0.8rem 0.9rem',
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>目標對象信號</label>
                                        <textarea
                                            rows={8}
                                            value={audienceSignalsText}
                                            onChange={(e) => setAudienceSignalsText(e.target.value)}
                                            style={{
                                                width: '100%',
                                                borderRadius: '12px',
                                                border: '1px solid var(--color-border)',
                                                padding: '0.8rem 0.9rem',
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div
                                        style={{
                                            borderRadius: '14px',
                                            background: 'var(--color-bg-secondary)',
                                            padding: '1rem'
                                        }}
                                    >
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>Asset Group Brief</div>
                                        <div style={{ marginBottom: '0.4rem' }}><strong>主題：</strong>{plan.assetGroupPlan.themeName}</div>
                                        <div style={{ marginBottom: '0.4rem' }}><strong>視覺方向：</strong>{plan.assetGroupPlan.visualDirection}</div>
                                        <div style={{ marginBottom: '0.4rem' }}><strong>文案開場軸：</strong>{plan.assetGroupPlan.copyOpeningAxis}</div>
                                        <div style={{ marginBottom: '0.4rem' }}><strong>Proof Pool：</strong>{plan.assetGroupPlan.proofPool.join('、')}</div>
                                        <div><strong>禁止混寫：</strong>{plan.assetGroupPlan.forbiddenMixes.join('、')}</div>
                                    </div>
                                    <div
                                        style={{
                                            borderRadius: '14px',
                                            background: 'var(--color-bg-secondary)',
                                            padding: '1rem'
                                        }}
                                    >
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>Landing Page</div>
                                        <div style={{ marginBottom: '0.4rem' }}><strong>頁面：</strong>{plan.landingPagePlan.title}</div>
                                        <div style={{ marginBottom: '0.4rem' }}><strong>URL：</strong>{plan.landingPagePlan.url}</div>
                                        <div style={{ marginBottom: '0.4rem' }}><strong>Hero：</strong>{plan.landingPagePlan.heroAngle}</div>
                                        <div><strong>Support Proofs：</strong>{plan.landingPagePlan.supportProofs.join('、')}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>名人素材</label>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {(referenceLibrary?.celebrities.active || []).map((celebrity) => (
                                                <button
                                                    key={celebrity.id}
                                                    onClick={() => toggleNamedValue(celebrity.name, celebrityNames, setCelebrityNames)}
                                                    style={{
                                                        border: `1px solid ${celebrityNames.includes(celebrity.name) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                        background: celebrityNames.includes(celebrity.name) ? 'var(--color-primary-light)' : 'var(--color-bg)',
                                                        borderRadius: '999px',
                                                        padding: '0.35rem 0.7rem',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {celebrity.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem' }}>活動素材</label>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {(referenceLibrary?.activities.active || []).map((activity) => (
                                                <button
                                                    key={activity.id}
                                                    onClick={() => toggleNamedValue(activity.name, activityNames, setActivityNames)}
                                                    style={{
                                                        border: `1px solid ${activityNames.includes(activity.name) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                        background: activityNames.includes(activity.name) ? 'var(--color-primary-light)' : 'var(--color-bg)',
                                                        borderRadius: '999px',
                                                        padding: '0.35rem 0.7rem',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {activity.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ borderRadius: '14px', background: 'var(--color-bg-secondary)', padding: '1rem', marginBottom: '1rem' }}>
                                    <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>翻譯原則</div>
                                    <ul style={{ margin: 0, paddingInlineStart: '1.1rem', lineHeight: 1.8 }}>
                                        {plan.searchSignalPlan.translationNotes.map((note) => (
                                            <li key={note}>{note}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div style={{ display: 'flex', gap: '0.75rem' }}>
                                    <button
                                        onClick={() => setStep(3)}
                                        style={{
                                            border: '1px solid var(--color-border)',
                                            background: 'transparent',
                                            borderRadius: '12px',
                                            padding: '0.8rem 1rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        上一步
                                    </button>
                                    <button
                                        onClick={() => void buildPlan(4)}
                                        disabled={planLoading}
                                        style={{
                                            border: '1px solid var(--color-border)',
                                            background: 'var(--color-bg)',
                                            borderRadius: '12px',
                                            padding: '0.8rem 1rem',
                                            cursor: planLoading ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        重新整理規劃
                                    </button>
                                    <button
                                        onClick={() => void generateAssets()}
                                        disabled={generating}
                                        style={{
                                            border: 'none',
                                            background: 'var(--color-primary)',
                                            color: '#fff',
                                            borderRadius: '12px',
                                            padding: '0.8rem 1rem',
                                            cursor: generating ? 'not-allowed' : 'pointer',
                                            opacity: generating ? 0.65 : 1
                                        }}
                                    >
                                        {generating ? '生成中...' : '生成 Creative Pack'}
                                    </button>
                                </div>
                            </SurfaceCard>

                            <SurfaceCard>
                                <SectionTitle>預覽文案草圖</SectionTitle>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>短標題</div>
                                        <div style={{ display: 'grid', gap: '0.35rem' }}>
                                            {plan.copyPlan.shortHeadlines.map((headline, index) => (
                                                <div key={`${headline}-${index}`} style={{ color: 'var(--color-text-secondary)' }}>
                                                    {index + 1}. {headline}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>長標題</div>
                                        <div style={{ display: 'grid', gap: '0.35rem' }}>
                                            {plan.copyPlan.longHeadlines.map((headline, index) => (
                                                <div key={`${headline}-${index}`} style={{ color: 'var(--color-text-secondary)' }}>
                                                    {index + 1}. {headline}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>說明</div>
                                        <div style={{ display: 'grid', gap: '0.35rem' }}>
                                            {plan.copyPlan.descriptions.map((description, index) => (
                                                <div key={`${description}-${index}`} style={{ color: 'var(--color-text-secondary)' }}>
                                                    {index + 1}. {description}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </SurfaceCard>
                        </div>
                    )}

                    {step === 5 && plan && (
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <SurfaceCard>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                                    <SectionTitle>Asset Group Brief</SectionTitle>
                                    {generatedAssets && (
                                        <button
                                            onClick={() => void copyCreativePack()}
                                            style={{
                                                border: '1px solid var(--color-border)',
                                                background: copied ? 'var(--color-primary-light)' : 'var(--color-bg)',
                                                borderRadius: '12px',
                                                padding: '0.65rem 0.9rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {copied ? '已複製' : '複製整包素材'}
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <div style={{ marginBottom: '0.5rem' }}><strong>入口：</strong>{plan.entryPointPlan.entryPoint}</div>
                                        <div style={{ marginBottom: '0.5rem' }}><strong>場景：</strong>{plan.scenarioPlan.scenarioLabel}</div>
                                        <div style={{ marginBottom: '0.5rem' }}><strong>Persona：</strong>{plan.entryPointPlan.persona}</div>
                                        <div><strong>LP 匹配：</strong>{plan.landingPagePlan.matchStatus}</div>
                                    </div>
                                    <div>
                                        <div style={{ marginBottom: '0.5rem' }}><strong>主題：</strong>{plan.assetGroupPlan.themeName}</div>
                                        <div style={{ marginBottom: '0.5rem' }}><strong>視覺：</strong>{plan.assetGroupPlan.visualDirection}</div>
                                        <div><strong>Proof Pool：</strong>{plan.assetGroupPlan.proofPool.join('、')}</div>
                                    </div>
                                </div>
                                <div style={{ marginTop: '1rem', color: 'var(--color-text-secondary)' }}>
                                    Generation ID：{generationId || '尚未建立'} ｜ 狀態：{generationStatus}
                                </div>
                                <div style={{ marginTop: '0.5rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                                    目前階段：{GENERATION_PHASE_LABELS[generationPhase]}
                                </div>
                            </SurfaceCard>

                            <SurfaceCard>
                                <SectionTitle>Search Themes / Audience Signals</SectionTitle>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>搜尋主題</div>
                                        <div style={{ display: 'grid', gap: '0.35rem' }}>
                                            {parseMultilineList(searchThemesText).map((theme) => (
                                                <div key={theme}>{theme}</div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>目標對象信號</div>
                                        <div style={{ display: 'grid', gap: '0.35rem' }}>
                                            {parseMultilineList(audienceSignalsText).map((signal) => (
                                                <div key={signal}>{signal}</div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </SurfaceCard>

                            <SurfaceCard>
                                <SectionTitle>Asset Matrix</SectionTitle>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem' }}>
                                    {assetProgress.map((asset) => (
                                        <div
                                            key={asset.assetId}
                                            style={{
                                                border: '1px solid var(--color-border)',
                                                borderRadius: '12px',
                                                padding: '0.75rem 0.9rem',
                                                background: asset.status === 'completed'
                                                    ? 'rgba(236, 253, 245, 0.85)'
                                                    : asset.status === 'rejected'
                                                        ? 'rgba(254, 242, 242, 0.9)'
                                                        : 'var(--color-bg)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.35rem' }}>
                                                <strong>{asset.assetId}</strong>
                                                <span>{asset.status}</span>
                                            </div>
                                            <div style={{ fontSize: '0.92rem', color: 'var(--color-text-secondary)', marginBottom: '0.35rem' }}>
                                                嘗試次數：{asset.attemptIndex}
                                            </div>
                                            <div style={{ fontSize: '0.92rem', marginBottom: '0.35rem' }}>
                                                {asset.currentText || asset.slotContract}
                                            </div>
                                            {asset.rejectionReasons.length > 0 && (
                                                <div style={{ fontSize: '0.82rem', color: '#b91c1c', lineHeight: 1.5 }}>
                                                    {asset.rejectionReasons.join('｜')}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </SurfaceCard>

                            {generatedAssets && (
                                <SurfaceCard>
                                <SectionTitle>Creative Pack</SectionTitle>
                                <div style={{ display: 'grid', gap: '1rem' }}>
                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>短標題 15 則</div>
                                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                                            {generatedAssets.headlines.map((headline) => (
                                                <div
                                                    key={headline.id}
                                                    style={{
                                                        border: '1px solid var(--color-border)',
                                                        borderRadius: '12px',
                                                        padding: '0.75rem 0.9rem'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', marginBottom: '0.4rem' }}>
                                                        <strong>{headline.type}</strong>
                                                        {headline.qualityScore && <QualityBadge score={headline.qualityScore.overall} />}
                                                    </div>
                                                    <div style={{ marginBottom: '0.35rem' }}>{headline.text}</div>
                                                    {headline.qualityScore && <QualityDetails qualityScore={headline.qualityScore} />}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>長標題 5 則</div>
                                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                                            {(generatedAssets.longHeadlines || []).map((headline) => (
                                                <div
                                                    key={headline.id}
                                                    style={{
                                                        border: '1px solid var(--color-border)',
                                                        borderRadius: '12px',
                                                        padding: '0.75rem 0.9rem'
                                                    }}
                                                >
                                                    {headline.text}
                                                    <div style={{ marginTop: '0.25rem', color: 'var(--color-text-secondary)' }}>{headline.charCount} 字</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>說明 5 則</div>
                                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                                            {(generatedAssets.marketingDescriptions || []).map((description) => (
                                                <div
                                                    key={description.id}
                                                    style={{
                                                        border: '1px solid var(--color-border)',
                                                        borderRadius: '12px',
                                                        padding: '0.75rem 0.9rem'
                                                    }}
                                                >
                                                    {description.text}
                                                    <div style={{ marginTop: '0.25rem', color: 'var(--color-text-secondary)' }}>{description.charCount} 字</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                </SurfaceCard>
                            )}

                            {metadata && (
                                <SurfaceCard>
                                    <SectionTitle>生成資訊</SectionTitle>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
                                        <div><strong>模型：</strong>{metadata.modelUsed}</div>
                                        <div><strong>耗時：</strong>{metadata.generationTime.toFixed(1)}s</div>
                                        <div><strong>LLM 呼叫：</strong>{metadata.totalLLMCalls}</div>
                                        <div><strong>H1 特式化：</strong>{metadata.strategyUsed?.h1Specialization || plan.generationContext.specialization}</div>
                                    </div>
                                </SurfaceCard>
                            )}
                        </div>
                    )}

                    {error && (
                        <div
                            style={{
                                borderRadius: '12px',
                                background: '#fff5f5',
                                color: '#c53030',
                                border: '1px solid #feb2b2',
                                padding: '0.9rem 1rem'
                            }}
                        >
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
